import * as React from 'react'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector, useDispatch } from 'react-redux'

import Button from '../../ui/button'
import TextField from '../../form/TextField'
import SelectField from '../../form/SelectField'
import TextAreaField from '../../form/TextAreaField'
import CheckboxField from '../../form/CheckboxField'
import LocationSelect from '../../form/LocationSelect'
import AvatarUploader from '../../profile/avatarUploader'

import ChangeEmailPopup from '../../profile/popups/changeEmailPopup'
import ChangePasswordPopup from '../../profile/popups/changePasswordPopup'
import ChangeNickPopup from '../../profile/popups/changeNickPopup'

import { Field, FieldProps, Form, Formik, FormikProps } from 'formik'

import { RootState } from '../../../store/rootReducer'
import { UpdateProfile } from '../../../store/member/memberTypings'

import { profileUpdate } from '../../../store/member/memberSlice'
import { genders } from '../../../constants/index'
import s from '../profileInfo/profileInfo.module.sass'

interface Props {
    onClose: () => void
}

export type TValues = {
    age: number
    about: string
    gender: number
    region: string
    country: string
    internal_messaging: number
}

const ProfileEdit: React.FunctionComponent<Props> = ({ onClose }: Props) => {
    const [t] = useTranslation()
    const dispatch = useDispatch()

    const formikRef = useRef<FormikProps<TValues>>(null)

    const { user } = useSelector((state: RootState) => state.member)

    const [showChangeNick, setShowChangeNick] = useState(false)
    const [showChangeEmail, setShowChangeEmail] = useState(false)
    const [showChangePassword, setShowChangePassword] = useState(false)

    const initialValues: TValues = {
        age: user.age,
        about: user.about,
        gender: user.gender,
        region: user.region,
        country: user.country,
        internal_messaging: user.internal_messaging
    }

    return (
        <>
            <Formik
                innerRef={formikRef}
                initialValues={initialValues}
                onSubmit={(values: TValues): void => {
                    const params: UpdateProfile = {
                        age: values.age,
                        about: values.about,
                        gender: values.gender,
                        region: values.region,
                        user_country_code: values.country,
                        internal_messaging: values.internal_messaging
                    }
                    dispatch(profileUpdate(params))
                    onClose()
                }}
            >
                {(props: FormikProps<UpdateProfile>) => {
                    const { submitForm } = props
                    return (
                        <Form>
                            <div className={s['profileInfo']}>
                                <div className={s['profileInfo__avatar']}>
                                    <img src={user.avatar_url} alt="" />
                                    <AvatarUploader />
                                    <div className={s['profileInfo__changeButtons']}>
                                        <Button
                                            type="button"
                                            tertiary
                                            label={t('Change password')}
                                            onClick={() => setShowChangePassword(true)}
                                        />
                                        <Button
                                            type="button"
                                            tertiary
                                            label={t('Change email')}
                                            onClick={() => setShowChangeEmail(true)}
                                        />
                                    </div>
                                </div>
                                <div className={s['profileInfo__nick']}>
                                    {user.nick}
                                    <div className="edit" />
                                </div>

                                <div className={s['profileInfo__firstRow']}>
                                    <Field name="gender">
                                        {({ field, form }: FieldProps) => (
                                            <SelectField
                                                form={form}
                                                field={field}
                                                label={t('Gender')}
                                                options={genders}
                                            />
                                        )}
                                    </Field>
                                    <Field name="age">
                                        {({ field, meta }: FieldProps) => (
                                            <TextField
                                                field={field}
                                                type="number"
                                                label={t('Age')}
                                                placeholder="Age"
                                                error={meta.touched && meta.error}
                                            />
                                        )}
                                    </Field>
                                </div>
                                <div className={s['profileInfo__secondRow']}>
                                    <div className={s['locationSelect']}>
                                        <LocationSelect
                                            initialCountry={user.country}
                                            formikRef={formikRef}
                                            initialRegion={user.region}
                                        />
                                    </div>
                                </div>
                                <div className={s['profileInfo__thirdRow']}>
                                    <Field name="about">
                                        {({ field, meta, form }: FieldProps) => (
                                            <TextAreaField
                                                form={form}
                                                field={field}
                                                label={t('About')}
                                                error={meta.touched && meta.error}
                                                placeholder={t('Tell something about you')}
                                            />
                                        )}
                                    </Field>
                                </div>
                                <div className={s['profileInfo__fourthRow']}>
                                    <Field name="internal_messaging">
                                        {({ field, form }: FieldProps) => (
                                            <CheckboxField
                                                form={form}
                                                field={field}
                                                label={t('Allow private messages')}
                                            />
                                        )}
                                    </Field>
                                    <div className={s['profileInfo__buttons']}>
                                        <Button label={t('Save')} type="button" small onClick={submitForm} />
                                        <Button label={t('Reset')} type="button" small secondary onClick={onClose} />
                                    </div>
                                </div>
                            </div>
                        </Form>
                    )
                }}
            </Formik>
            {showChangeNick && <ChangeNickPopup onClose={() => setShowChangeNick(false)} />}
            {showChangeEmail && <ChangeEmailPopup onClose={() => setShowChangeEmail(false)} />}
            {showChangePassword && <ChangePasswordPopup onClose={() => setShowChangePassword(false)} />}
        </>
    )
}

export default ProfileEdit
